import { DEFAULT_SETTINGS } from '../defaults'
import { createEffectsProcessor } from '../effects/EffectsProcessor'
import { DEFAULT_EFFECTS_STATE } from '../effects/types'
import type { EffectsState } from '../effects/types'
import { createRenderer } from '../Renderer'
import type { FullRenderSettings, MousePosition, RenderResult } from '../types'
import type { FitMode } from './ResolutionPresets'

export interface RenderFrameRequest {
  sourceImage: HTMLImageElement | HTMLCanvasElement | ImageData
  settings?: Partial<FullRenderSettings>
  effectsState?: EffectsState
  width: number
  height: number
  frameIndex: number
  totalFrames: number
  timeMs: number
  fitMode?: FitMode
}

export interface RenderFrameResponse {
  canvas: HTMLCanvasElement
  renderResult: RenderResult
}

export interface DrawRect {
  x: number
  y: number
  width: number
  height: number
}

export function createDefaultMouseState(): MousePosition {
  return {
    x: 0,
    y: 0,
    isInCanvas: false,
  }
}

export function getSourceDimensions(source: HTMLImageElement | HTMLCanvasElement | ImageData): { width: number; height: number } {
  if ('naturalWidth' in source) {
    return {
      width: source.naturalWidth,
      height: source.naturalHeight,
    }
  }

  return {
    width: source.width,
    height: source.height,
  }
}

export function calculateFittedDrawRect(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number,
  fitMode: FitMode = 'contain'
): DrawRect {
  if (fitMode === 'stretch' || sourceWidth <= 0 || sourceHeight <= 0) {
    return {
      x: 0,
      y: 0,
      width: targetWidth,
      height: targetHeight,
    }
  }

  const scaleX = targetWidth / sourceWidth
  const scaleY = targetHeight / sourceHeight
  const scale = fitMode === 'cover'
    ? Math.max(scaleX, scaleY)
    : Math.min(scaleX, scaleY)

  const width = sourceWidth * scale
  const height = sourceHeight * scale

  return {
    x: Math.round((targetWidth - width) / 2),
    y: Math.round((targetHeight - height) / 2),
    width: Math.round(width),
    height: Math.round(height),
  }
}

function normalizeSourceToCanvas(source: HTMLImageElement | HTMLCanvasElement | ImageData): HTMLCanvasElement {
  if (source instanceof HTMLCanvasElement) {
    return source
  }

  const canvas = document.createElement('canvas')
  const { width, height } = getSourceDimensions(source)
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Failed to create source normalization canvas')
  }

  if (source instanceof ImageData) {
    ctx.putImageData(source, 0, 0)
  } else {
    ctx.drawImage(source, 0, 0, width, height)
  }

  return canvas
}

export class FrameRenderer {
  private readonly renderer = createRenderer()
  private readonly effectsProcessor = createEffectsProcessor()

  createTargetCanvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(width))
    canvas.height = Math.max(1, Math.round(height))
    return canvas
  }

  prepareSourceCanvas(
    source: HTMLImageElement | HTMLCanvasElement | ImageData,
    targetWidth: number,
    targetHeight: number,
    fitMode: FitMode,
    backgroundColor: string
  ): HTMLCanvasElement {
    const normalizedSource = normalizeSourceToCanvas(source)
    const preparedCanvas = this.createTargetCanvas(targetWidth, targetHeight)
    const preparedCtx = preparedCanvas.getContext('2d')

    if (!preparedCtx) {
      throw new Error('Failed to create prepared source context')
    }

    preparedCtx.fillStyle = backgroundColor
    preparedCtx.fillRect(0, 0, targetWidth, targetHeight)

    const rect = calculateFittedDrawRect(
      normalizedSource.width,
      normalizedSource.height,
      targetWidth,
      targetHeight,
      fitMode
    )

    preparedCtx.imageSmoothingEnabled = true
    preparedCtx.imageSmoothingQuality = 'high'
    preparedCtx.drawImage(
      normalizedSource,
      rect.x,
      rect.y,
      rect.width,
      rect.height
    )

    return preparedCanvas
  }

  renderFrame(request: RenderFrameRequest): RenderFrameResponse {
    const settings: FullRenderSettings = {
      ...DEFAULT_SETTINGS,
      ...request.settings,
    }
    const fitMode = request.fitMode ?? 'contain'
    const preparedSource = this.prepareSourceCanvas(
      request.sourceImage,
      request.width,
      request.height,
      fitMode,
      settings.backgroundColor
    )
    const canvas = this.createTargetCanvas(request.width, request.height)
    const effectsState = request.effectsState ?? DEFAULT_EFFECTS_STATE
    const timeSec = request.timeMs / 1000
    const deltaTimeSec = request.frameIndex === 0 ? 0 : timeSec / request.frameIndex

    const renderResult = this.renderer.render(
      preparedSource,
      canvas,
      settings,
      {
        time: timeSec,
        deltaTime: deltaTimeSec,
        frameCount: request.frameIndex,
        mouseState: createDefaultMouseState(),
      }
    )

    this.effectsProcessor.reset()
    this.effectsProcessor.syncEffectsFromState(effectsState)
    this.effectsProcessor.update(timeSec)
    this.effectsProcessor.apply(renderResult.canvas, effectsState)

    return {
      canvas: renderResult.canvas,
      renderResult,
    }
  }
}

export function createFrameRenderer(): FrameRenderer {
  return new FrameRenderer()
}
